import { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, AlertTriangle, ShieldCheck, Trash2 } from 'lucide-react';
import { useCancelAppointment, useForceAppointment } from '@/api/resources';
import { useCounterReadOnly } from '@/lib/useCounterSession';
import { Button } from '@/components/ui/Button';
import type { Appointment } from '@/types';
import { SKILL_LABELS, VERDICT_STYLE, formatSlot, appointmentLabel } from './AppointmentsPage';

/**
 * Détail d'un RDV. La couverture affichée vient du recalcul backend : elle
 * reflète l'état du planning **maintenant**, pas au moment de la création.
 */
export default function AppointmentDetailModal({
  appointment: a,
  onClose,
}: {
  appointment: Appointment;
  onClose: () => void;
}) {
  const cov = a.coverage;
  const verdict = cov?.verdict ?? 'NONE';
  const cancelled = a.status === 'CANCELLED';

  const [forcing, setForcing] = useState(false);
  const [employeeId, setEmployeeId] = useState(a.assignedEmployeeId ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const force = useForceAppointment();
  const cancel = useCancelAppointment();
  // Le comptoir prend et annule des rendez-vous. Désigner quelqu'un MALGRÉ
  // l'alerte reste une décision du titulaire : elle s'assume et se justifie.
  const readOnly = useCounterReadOnly();

  // Une désignation exceptionnelle n'a de sens que vers quelqu'un ; on propose
  // les qualifiés du moment, à défaut c'est une saisie libre côté planning.
  const options = cov?.eligible ?? [];

  async function submitForce() {
    setError(null);
    try {
      await force.mutateAsync({ id: a.id, employeeId, forcedReason: reason.trim() });
      setForcing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Le forçage a échoué.');
    }
  }

  async function submitCancel() {
    if (!confirm('Annuler ce rendez-vous ?')) return;
    await cancel.mutateAsync(a.id);
    onClose();
  }

  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-[13px]';
  const label = 'text-[11px] font-medium text-slate-500 mb-1 block';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
        >
          <X size={16} />
        </button>

        <h2 className="text-[15px] font-semibold text-slate-900">{appointmentLabel(a)}</h2>
        <p className="text-[12px] text-slate-500 mb-4 tabular-nums">
          {format(new Date(a.startAt), 'EEEE d MMMM yyyy', { locale: fr })} ·{' '}
          {formatSlot(a.startAt)} · {a.durationMinutes} min
        </p>

        {cancelled ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-500 mb-3">
            Rendez-vous annulé.
          </div>
        ) : (
          <div
            className={`rounded-lg border px-3 py-2.5 mb-3 ${VERDICT_STYLE[verdict].block} pointer-events-none`}
          >
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              {verdict === 'ALERT' && <AlertTriangle size={13} />}
              {verdict === 'FORCED' && <ShieldCheck size={13} />}
              {VERDICT_STYLE[verdict].label}
              {cov?.requiredSkill && (
                <span className="font-normal opacity-80">
                  · compétence {SKILL_LABELS[cov.requiredSkill] ?? cov.requiredSkill}
                </span>
              )}
            </div>
            {verdict === 'ALERT' && (
              <div className="text-[11px] mt-1 opacity-90">
                Personne de qualifié n'est présent et libre sur ce créneau.
              </div>
            )}
            {verdict === 'REASSIGN' && (
              <div className="text-[11px] mt-1 opacity-90">
                {cov!.eligible.map((e) => `${e.firstName} ${e.lastName[0]}.`).join(', ')} peut
                {cov!.eligible.length > 1 ? 'vent' : ''} prendre ce rendez-vous.
              </div>
            )}
            {verdict === 'OK' && cov!.eligible.length > 0 && (
              <div className="text-[11px] mt-1 opacity-90">
                {cov!.eligible.length} personne{cov!.eligible.length > 1 ? 's' : ''} qualifiée
                {cov!.eligible.length > 1 ? 's' : ''} disponible
                {cov!.eligible.length > 1 ? 's' : ''}.
              </div>
            )}
          </div>
        )}

        <dl className="space-y-1.5 text-[12px] mb-4">
          <div className="flex justify-between">
            <dt className="text-slate-500">Affecté à</dt>
            <dd className="text-slate-900 font-medium">
              {a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : '— non affecté'}
            </dd>
          </div>
          {a.patientName && (
            <div className="flex justify-between">
              <dt className="text-slate-500">Patient</dt>
              <dd className="text-slate-900">{a.patientName}</dd>
            </div>
          )}
          {a.patientPhone && (
            <div className="flex justify-between">
              <dt className="text-slate-500">Téléphone</dt>
              <dd className="text-slate-900 tabular-nums">{a.patientPhone}</dd>
            </div>
          )}
          {a.notes && (
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500 shrink-0">Remarque</dt>
              <dd className="text-slate-900 text-right">{a.notes}</dd>
            </div>
          )}
        </dl>

        {a.forcedReason && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 mb-4">
            <div className="text-[11px] font-semibold text-violet-800">
              Désignation exceptionnelle
            </div>
            <div className="text-[12px] text-violet-900 mt-0.5">{a.forcedReason}</div>
          </div>
        )}

        {/* ---- Forçage ---- */}
        {!cancelled && forcing && (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div>
              <label className={label}>Désigner</label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className={field}
              >
                <option value="">— Choisir —</option>
                {options.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.firstName} {e.lastName}
                  </option>
                ))}
                {/* L'assigné actuel reste proposé même s'il n'est plus qualifié. */}
                {a.employee && !options.some((o) => o.id === a.employee!.id) && (
                  <option value={a.employee.id}>
                    {a.employee.firstName} {a.employee.lastName} (actuel)
                  </option>
                )}
              </select>
            </div>
            <div>
              <label className={label}>Motif *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Ex. : formation TROD en cours, supervision par le titulaire."
                className={field}
              />
            </div>
            {error && <div className="text-[11px] text-red-600">{error}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="default" onClick={() => setForcing(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={submitForce}
                disabled={!employeeId || reason.trim().length < 5 || force.isPending}
              >
                {force.isPending ? 'Validation…' : 'Valider la désignation'}
              </Button>
            </div>
          </div>
        )}

        {!cancelled && !forcing && (
          <div className="flex justify-between gap-2 border-t border-slate-200 pt-3">
            <button
              onClick={submitCancel}
              className="inline-flex items-center gap-1.5 text-[12px] text-red-600 hover:bg-red-50 rounded-lg px-2 py-1.5"
            >
              <Trash2 size={13} />
              Annuler le RDV
            </button>
            {!readOnly && (
              <Button variant="default" icon={ShieldCheck} onClick={() => setForcing(true)}>
                Désigner à titre exceptionnel
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
